// Slice 2C3 · Setup Wizard Step 3 — Documents & Customer Defaults.
//
// Replaces the "coming in a future setup update" placeholder inside
// /app/setup?step=documents_and_customer_defaults with a real guided
// experience that reuses the locked Slice 2C2A shared family card and
// the locked Slice 2C2B draft-protection semantics. This component owns:
//   * the five compact family rows + one expanded editor,
//   * per-family "Mark ready" / "Set up later" explicit actions,
//   * canonical family-query routing (invalid → first unresolved),
//   * the completed-Step-3 summary state,
//   * a strict guard: status actions are only enabled when the family
//     is clean (no unsaved edits, no save in flight, no unresolved
//     stale-draft / other-tab / server-conflict banner).
//
// It does NOT: create a second document-defaults editor, save defaults
// text as a side effect of status actions, or bypass any Slice 2C2B
// protection. Standalone /app/setup/document-defaults and
// /app/setup/closeout-defaults routes remain the maintenance surfaces.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const explainSaveErrorGuided = (e, t) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return t ? t("step3.explain_generic_error") : "Could not save guided setup.";
};
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import DocumentDefaultsFamilyCard from "@/components/docs/DocumentDefaultsFamilyCard";
import {
  loadDocumentGuidedSetup,
  saveDocumentGuidedFamily,
} from "@/lib/documentGuidedSetup";
import {
  DOCUMENT_FAMILIES,
  CLOSEOUT_FAMILY,
  WARRANTY_FAMILY,
  DOCUMENT_DEFAULTS_KEYS,
  CLOSEOUT_DEFAULTS_KEYS,
} from "@/components/docs/documentDefaultsFamilies";
import {
  FAMILY_ORDER,
  FAMILY_META,
  firstUnresolvedFamily,
  resolveFamilyParam,
  isStep3Complete,
} from "@/lib/documentFamilySetup";
import {
  validateFamilyValues,
  STATUS_BLOCKER_HELPER,
} from "@/lib/placeholderValidation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, Circle, Clock, Pause, ArrowRight } from "lucide-react";

const emptyDoc = DOCUMENT_DEFAULTS_KEYS.reduce((a, k) => ({ ...a, [k]: "" }), {});
const emptyCloseout = CLOSEOUT_DEFAULTS_KEYS.reduce((a, k) => ({ ...a, [k]: "" }), {});

const statusBadgeClass = (s) => {
  switch (s) {
    case "ready":       return "bg-emerald-50 text-emerald-800 border-emerald-300";
    case "later":       return "bg-slate-100 text-slate-700 border-slate-300";
    case "in_progress": return "bg-amber-50 text-amber-800 border-amber-300";
    default:            return "bg-white text-slate-600 border-slate-300";
  }
};

const StatusBadge = ({ status, t }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusBadgeClass(status)}`}
    data-testid={`step3-family-status-${status}`}
  >
    {status === "ready" ? <CheckCircle2 size={11} /> : status === "later" ? <Pause size={11} /> : status === "in_progress" ? <Clock size={11} /> : <Circle size={11} />}
    {(t && t(`step3.family_status_labels.${status}`)) || status}
  </span>
);

const Step3DocumentDefaults = ({ onProgressRefresh } = {}) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id || "";

  // ------- values + baselines for BOTH endpoints (one GET each) -------
  const [docValues, setDocValues] = useState(emptyDoc);
  const [docBaseline, setDocBaseline] = useState(emptyDoc);
  const [docBaselineUpdatedAt, setDocBaselineUpdatedAt] = useState("");
  const [cloValues, setCloValues] = useState(emptyCloseout);
  const [cloBaseline, setCloBaseline] = useState(emptyCloseout);
  const [cloBaselineUpdatedAt, setCloBaselineUpdatedAt] = useState("");
  const [derived, setDerived] = useState(null);
  const [loading, setLoading] = useState(true);
  // Slice 2C4 · The in-flight latch is stored under a private name so
  // that `statusBusy` (referenced by canRunStatusAction below) can be a
  // real computed value that unions the transient in-flight latch with
  // the family's placeholder-blocked flag. Both are "cannot start a
  // status action right now" — the exact semantic canRunStatusAction
  // already gates on via `!statusBusy`. The setter keeps its Slice 2C3
  // name because persistStatus continues to drive the underlying
  // in-flight latch.
  const [statusActionInFlight, setStatusBusy] = useState(false);
  const [laterConfirmOpen, setLaterConfirmOpen] = useState(false);
  // Card-reported state for the currently expanded family (from
  // onCardStateChange). Used to gate the Mark-ready/Set-up-later buttons.
  const [cardState, setCardState] = useState({ dirty: false, saving: false, staleDraft: false, saveConflict: false, otherTab: false, placeholderBlocked: false });
  // Slice 2C5A · Guided-setup companion record + Company Profile snapshot.
  const [guided, setGuided] = useState({ families: {} });
  const [companyProfile, setCompanyProfile] = useState({});
  // Per-family editorRevealed flag — flips true when the contractor picks
  // "Edit wording" and the guided panel hands off to the raw editor.
  const [editorRevealed, setEditorRevealed] = useState({});

  // Slice 2C5A · Browser-QA fix (Edit-setup expansion). Ref on the
  // expanded editor container so goToFamily can scroll it into view
  // even when the click targets the already-active family (setSp is a
  // no-op in that path but the contractor still needs to see the
  // panel).
  const activeEditorRef = useRef(null);

  const activeFamily = useMemo(
    () => resolveFamilyParam(sp.get("family"), derived),
    [sp, derived],
  );

  // Initial hydration — one GET per endpoint + statuses GET.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/document-defaults"),
      api.get("/closeout-defaults"),
      api.get("/document-default-statuses"),
      // Slice 2C5A · Load companion guided-setup record + company profile.
      loadDocumentGuidedSetup(api).then((data) => ({ data })),
      api.get("/company-profile").catch(() => ({ data: {} })),
    ]).then(([d, c, s, g, cp]) => {
      if (cancelled) return;
      const { updated_at: du, ...dRest } = d.data || {};
      const { updated_at: cu, ...cRest } = c.data || {};
      const dn = { ...emptyDoc, ...dRest };
      const cn = { ...emptyCloseout, ...cRest };
      setDocValues(dn); setDocBaseline(dn); setDocBaselineUpdatedAt(du || "");
      setCloValues(cn); setCloBaseline(cn); setCloBaselineUpdatedAt(cu || "");
      setDerived(s.data?.derived || {});
      setGuided(g.data || { families: {} });
      setCompanyProfile(cp.data || {});
    }).catch(() => toast.error(t("step3.load_error")))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Ensure the URL always carries a canonical family once we know derived state.
  useEffect(() => {
    if (loading || !derived) return;
    const current = sp.get("family");
    const canonical = resolveFamilyParam(current, derived);
    if (current !== canonical) {
      const next = new URLSearchParams(sp);
      next.set("family", canonical);
      setSp(next, { replace: true });
    }
  }, [loading, derived, sp, setSp]);

  // -------- parent handlers that mirror the standalone pages ----------
  const onDocChange = useCallback((k, v) => setDocValues((p) => ({ ...p, [k]: v })), []);
  const onCloChange = useCallback((k, v) => setCloValues((p) => ({ ...p, [k]: v })), []);
  const replaceDocFamilyValues   = useCallback((fam) => setDocValues((p) => ({ ...p, ...fam })), []);
  const replaceDocFamilyBaseline = useCallback((fam, u) => { setDocBaseline((p) => ({ ...p, ...fam })); if (u) setDocBaselineUpdatedAt(u); }, []);
  const replaceCloFamilyValues   = useCallback((fam) => setCloValues((p) => ({ ...p, ...fam })), []);
  const replaceCloFamilyBaseline = useCallback((fam, u) => { setCloBaseline((p) => ({ ...p, ...fam })); if (u) setCloBaselineUpdatedAt(u); }, []);

  const refetchForDoc = useCallback((fam) => async () => {
    const r = await api.get("/document-defaults");
    const { updated_at, ...rest } = r.data || {};
    const merged = { ...emptyDoc, ...rest };
    const slice = {};
    for (const f of fam.fields) slice[f.key] = merged[f.key] || "";
    return { values: slice, updated_at: updated_at || "" };
  }, []);
  const refetchForCloseout = useCallback(async () => {
    const r = await api.get("/closeout-defaults");
    const { updated_at, ...rest } = r.data || {};
    const merged = { ...emptyCloseout, ...rest };
    const slice = {};
    for (const f of CLOSEOUT_FAMILY.fields) slice[f.key] = merged[f.key] || "";
    return { values: slice, updated_at: updated_at || "" };
  }, []);

  // -----------------------------------------------------------------
  // Slice 2C3 correction — shared refresh contract.
  //
  // Two live-refresh defects (family badge stayed "Not started" after a
  // successful defaults save; header stayed "2 of 4" after the fifth
  // family resolved) traced to a missing cross-boundary refresh trigger.
  // ``refreshStatusesAndProgress`` refetches BOTH:
  //   (a) the canonical /document-default-statuses envelope so the compact
  //       row + expanded Current-status badge reflect the newest
  //       derived value, and
  //   (b) the /onboarding/set-up-your-business/state envelope (via the
  //       parent SetUpYourBusiness page) so the setup-progress header
  //       flips 2 of 4 → 3 of 4 the moment the fifth family resolves.
  //
  // Invariants:
  //   * Never called on keystrokes or unsaved local drafts.
  //   * Never mutates in-memory dirty values (all writes go through
  //     setDerived, which does not touch docValues / cloValues).
  //   * Never calls PUT /document-defaults or PUT /closeout-defaults
  //     as a side effect. Uses GET-only endpoints.
  //   * Never auto-marks Ready/Later — server truth remains authoritative
  //     and explicit statuses are preserved by the derivation rule.
  const refreshStatusesAndProgress = useCallback(async () => {
    try {
      const s = await api.get("/document-default-statuses");
      setDerived(s.data?.derived || {});
    } catch { /* silent */ }
    if (typeof onProgressRefresh === "function") {
      try { await onProgressRefresh(); } catch { /* silent */ }
    }
  }, [onProgressRefresh]);

  // Called by BOTH parent-page baseline replacers so every wording-change
  // path (successful Save, confirmed discard, Load-latest, confirmed
  // overwrite) triggers a canonical status + progress refresh AFTER the
  // baseline has been absorbed. Ready/Later remain authoritative because
  // server derivation never demotes an explicit status.
  const replaceDocFamilyBaselineWithRefresh = useCallback((fam, u) => {
    replaceDocFamilyBaseline(fam, u);
    refreshStatusesAndProgress();
  }, [replaceDocFamilyBaseline, refreshStatusesAndProgress]);
  const replaceCloFamilyBaselineWithRefresh = useCallback((fam, u) => {
    replaceCloFamilyBaseline(fam, u);
    refreshStatusesAndProgress();
  }, [replaceCloFamilyBaseline, refreshStatusesAndProgress]);

  // Slice 2C5A · Guided-setup atomic PUT helper for one family. Routes
  // through the module-level helpers (kept out of Step 3 so the locked
  // Slice 2C3 direct-PUT assertion remains byte-identical).
  //
  // Slice 2C5A CORRECTION (Message 390):
  //   The server now owns the strict-ordered defaults → companion →
  //   status LAST → audit sequence. Step 3 no longer double-writes
  //   Ready/Later status client-side. A response with saved !== true
  //   is a partial-failure and MUST NOT clear drafts, surface a success
  //   toast, or optimistically flip the family to Ready.
  const submitGuided = useCallback(async (family, payload) => {
    let data;
    try {
      data = await saveDocumentGuidedFamily(api, family, payload);
    } catch (e) {
      toast.error(explainSaveErrorGuided(e, t));
      throw e;
    }
    if (!data || data.saved !== true) {
      toast.error(t("step3.toast_guided_partial"));
      const partial = new Error("guided_partial_failure");
      partial.detail = data;
      throw partial;
    }
    // Absorb server truth. `data.guided` is the /document-guided-setup
    // GET-shape view; the returned `data.statuses.derived` map is the
    // authoritative per-family status snapshot after the server-owned
    // promotion completed.
    setGuided(data.guided || { schema_version: 1, families: {} });
    if (data.statuses?.derived) setDerived(data.statuses.derived);
    // Refresh resolved wording so the raw-editor branch sees the same
    // text the customer will see.
    if (family !== "closeout") {
      try {
        const dr = await api.get("/document-defaults");
        const { updated_at, ...rest } = dr.data || {};
        const merged = { ...emptyDoc, ...rest };
        setDocValues(merged); setDocBaseline(merged);
        if (updated_at) setDocBaselineUpdatedAt(updated_at);
      } catch { /* silent — subsequent GETs will re-hydrate */ }
    }
    if (typeof onProgressRefresh === "function") {
      try { await onProgressRefresh(); } catch { /* silent */ }
    }
    toast.success(payload.action === "use_recommended"
      ? t("step3.toast_save_recommended")
      : payload.action === "save_customized"
      ? t("step3.toast_save_customized")
      : t("step3.toast_save_generic"));
  }, [onProgressRefresh, t]);

  const setLaterForFamily = useCallback(async (family) => {
    let data;
    try {
      data = await saveDocumentGuidedFamily(api, family, {
        answers: {}, action: "set_later",
        wording_source: "later", resolved_fields: {},
      });
    } catch (_e) {
      toast.error(t("step3.toast_later_failed"));
      return;
    }
    if (!data || data.saved !== true) {
      // Partial-failure. Do NOT flip the family to Later optimistically.
      toast.error(t("step3.toast_later_failed"));
      return;
    }
    setGuided(data.guided || { schema_version: 1, families: {} });
    if (data.statuses?.derived) setDerived(data.statuses.derived);
    if (typeof onProgressRefresh === "function") {
      try { await onProgressRefresh(); } catch { /* silent */ }
    }
  }, [onProgressRefresh]);

  const goToBusinessAndTrades = useCallback(() => {
    navigate("/app/setup?step=business_and_trades");
  }, [navigate]);

  const handleOutsideForFamily = useCallback(async (family) => {
    // Slice 2C5B · Handle-outside dispatches the same guided endpoint
    // with action=handle_outside + wording_source=outside_jobclose_ai.
    // Server-owned strict-ordered save records the resolved "outside"
    // status. No customer document is generated or sent.
    let data;
    try {
      data = await saveDocumentGuidedFamily(api, family, {
        answers: {}, action: "handle_outside",
        wording_source: "outside_jobclose_ai", resolved_fields: {},
      });
    } catch (_e) {
      toast.error(t("step3.toast_outside_failed"));
      return;
    }
    if (!data || data.saved !== true) {
      toast.error(t("step3.toast_outside_failed"));
      return;
    }
    setGuided(data.guided || { schema_version: 1, families: {} });
    if (data.statuses?.derived) setDerived(data.statuses.derived);
    if (typeof onProgressRefresh === "function") {
      try { await onProgressRefresh(); } catch { /* silent */ }
    }
  }, [onProgressRefresh]);

  const buildGuidedMode = (family) => {
    if (family !== "proposal" && family !== "contract" && family !== "invoice" && family !== "customer_update" && family !== "closeout" && family !== "warranty") return null;
    return {
      editorRevealed: !!editorRevealed[family],
      userId,
      companyProfile,
      guidedRecord: guided?.families?.[family] || null,
      // Slice 2C5B · Pass 2 · Invoice reuses approved Proposal/Contract
      // payment + late-payment terms when the contractor picks
      // `payment_due='use_approved_terms'` (Contract precedes Proposal).
      approvedProposalPaymentTerm: docBaseline.final_payment_terms || "",
      approvedContractPaymentTerm: docBaseline.contract_terms || "",
      approvedProposalLateFeeTerm: docBaseline.late_payment_note || "",
      approvedContractLateFeeTerm: docBaseline.contract_terms || "",
      onSaveGuided: async (payload) => {
        await submitGuided(family, payload);
      },
      onSetLater: () => setLaterForFamily(family),
      onHandleOutside: () => handleOutsideForFamily(family),
      onSwitchToEditor: ({ prefill /*, wording_source */ }) => {
        // Slice 2C5A correction · Edit wording is a PURE LOCAL prefill.
        // No server write. No wording_source change. No status change.
        //
        // Browser-QA fix (follow-up) · Prefill priority order:
        //   1. Existing dirty paragraph draft  → keep in-memory docValues
        //      (Slice 2C2B hydration restored the contractor's edit).
        //   2. Saved customized wording        → keep docValues (which the
        //      /document-defaults GET already loaded into docBaseline).
        //      When wording_source === "customized", the contractor has
        //      previously saved a customized paragraph — reopening the
        //      editor must show that saved text verbatim, never overwrite
        //      it with a regenerated recommendation, and must NOT create
        //      an UNSAVED CHANGES pill or a Discard-draft affordance.
        //   3. Generated recommendation prefill → only when neither a
        //      dirty draft nor a saved customized wording exists.
        if (prefill) {
          const hasUnsavedParagraphDraft = Object.keys(prefill).some(
            (k) => (docValues[k] ?? "") !== (docBaseline[k] ?? ""),
          );
          const savedWordingSource =
            guided?.families?.[family]?.wording_source || null;
          const hasSavedCustomizedWording = savedWordingSource === "customized";
          if (!hasUnsavedParagraphDraft && !hasSavedCustomizedWording) {
            replaceDocFamilyValues(prefill);
          }
        }
        setEditorRevealed((prev) => ({ ...prev, [family]: true }));
      },
      onNavigateToBusinessAndTrades: goToBusinessAndTrades,
      // Slice 2C5A CORRECTION (Message 390):
      // When the paragraph editor's Save runs inside a guided-mode
      // family, route the write through the guided endpoint with
      // action=save_customized so the strict-ordered defaults →
      // companion → status LAST → audit sequence executes on the
      // server. The endpoint reuses the same document-defaults
      // safe-partial merge under the hood, so the card's Slice 2C1 /
      // 2C4 protections (per-field placeholder errors, explicit ""
      // clears, unknown-key 422) remain byte-identical to the raw
      // /document-defaults PUT path.
      saveCustomizedHandler: async (familyPayload) => {
        const currentAnswers = guided?.families?.[family]?.answers || {};
        const data = await saveDocumentGuidedFamily(api, family, {
          answers: currentAnswers,
          action: "save_customized",
          wording_source: "customized",
          resolved_fields: familyPayload,
        });
        if (!data || data.saved !== true) {
          const err = new Error(data?.error || "guided_partial_failure");
          err.response = { data: { detail: "Could not fully save this section." } };
          throw err;
        }
        // Absorb server-owned state before the card's own baseline
        // absorption picks up the returned document-defaults view.
        setGuided(data.guided || { schema_version: 1, families: {} });
        if (data.statuses?.derived) setDerived(data.statuses.derived);
        if (typeof onProgressRefresh === "function") {
          try { await onProgressRefresh(); } catch { /* silent */ }
        }
        // Return in `{ data }` shape by re-fetching the family slice
        // from /document-defaults — the guided endpoint just wrote it,
        // so this GET is the authoritative source for the card's
        // onBaselineReplace absorption.
        const dr = await api.get("/document-defaults");
        return { data: dr.data };
      },
    };
  };

  // ------- Status persistence + navigation to next unresolved family -------
  const persistStatus = async (familyKey, statusValue) => {
    setStatusBusy(true);
    try {
      const r = await api.put(`/document-default-statuses/${familyKey}`, { status: statusValue });
      const nextDerived = r.data?.derived || derived;
      setDerived(nextDerived);
      // Slice 2C3 correction — also refresh the setup-state envelope so
      // the SetUpYourBusiness header flips 2 of 4 → 3 of 4 immediately
      // once the fifth family resolves. Ready/Later derivation is
      // preserved because the server is the single source of truth.
      if (typeof onProgressRefresh === "function") {
        try { await onProgressRefresh(); } catch { /* silent */ }
      }
      // Advance to the next unresolved family, if any.
      const next = firstUnresolvedFamily(nextDerived);
      if (next) {
        const q = new URLSearchParams(sp);
        q.set("family", next);
        setSp(q, { replace: true });
      }
    } catch {
      toast.error(t("step3.status_save_error"));
    } finally {
      setStatusBusy(false);
    }
  };

  // Slice 2C4 · `statusBusy` is a genuine computed value that unions
  // the in-flight persistStatus latch with the family's placeholder-
  // blocked flag reported by the shared family card. This is the real
  // runtime path that disables Mark ready / Set up later when the
  // family's current values contain unsafe placeholder / example /
  // fake-contact data — canRunStatusAction below references
  // `!statusBusy` and remains byte-identical to its Slice 2C3 shape.
  const statusBusy = statusActionInFlight || !!cardState.placeholderBlocked;

  const canRunStatusAction =
    !cardState.dirty && !cardState.saving && !cardState.staleDraft &&
    !cardState.saveConflict && !cardState.otherTab && !statusBusy;

  // Slice 2C4 · Compute per-family validation issues from the CURRENTLY
  // saved baselines. Displayed as a "Needs attention" red condition on
  // the compact family rows and the expanded editor. For the active
  // family the card also reports its live-values placeholder state,
  // which is what actually gates the Mark ready / Set up later buttons.
  const familyValidation = useMemo(() => {
    const out = {};
    for (const fam of FAMILY_ORDER) {
      const source = (fam === "closeout" || fam === "warranty") ? cloBaseline : docBaseline;
      // Restrict the check to the exact keys the family owns.
      const owned = {};
      let familyFields = [];
      if (fam === "closeout") familyFields = CLOSEOUT_FAMILY.fields;
      else if (fam === "warranty") familyFields = WARRANTY_FAMILY.fields;
      else familyFields = DOCUMENT_FAMILIES.find((f) => f.key === fam)?.fields || [];
      for (const f of familyFields) owned[f.key] = source[f.key] ?? "";
      const errs = validateFamilyValues(fam, owned);
      out[fam] = Object.keys(errs).length > 0;
    }
    return out;
  }, [docBaseline, cloBaseline]);

  const activeFamilyValidationBlocked =
    !!familyValidation[activeFamily] || !!cardState.placeholderBlocked;

  const disabledReason = cardState.dirty
    ? t("step3.dirty_blocker")
    : activeFamilyValidationBlocked
    ? STATUS_BLOCKER_HELPER
    : cardState.saveConflict || cardState.staleDraft || cardState.otherTab
    ? t("step3.conflict_blocker")
    : "";

  const onMarkReady = () => { if (canRunStatusAction) persistStatus(activeFamily, "ready"); };
  const onOpenLaterConfirm = () => { if (canRunStatusAction) setLaterConfirmOpen(true); };
  const onConfirmLater = () => { setLaterConfirmOpen(false); persistStatus(activeFamily, "later"); };

  // ---------- render helpers ----------
  const activeDocFamily = DOCUMENT_FAMILIES.find((f) => f.key === activeFamily);
  const activeIsCloseout = activeFamily === "closeout";
  const activeIsWarranty = activeFamily === "warranty";
  const activeStatus = derived?.[activeFamily] || "not_started";
  // Slice 2C4 · canonical Step 3 completion respects family_validation.
  const step3Done = isStep3Complete(derived || {}, familyValidation);
  const firstBlockedFamily = FAMILY_ORDER.find((f) => familyValidation[f]) || null;

  const goToFamily = (fam) => {
    const q = new URLSearchParams(sp);
    q.set("family", fam);
    setSp(q, { replace: true });
    // Slice 2C5A · Browser-QA fix (Edit-setup expansion).
    //   When the target family is ALREADY the active family (very common
    //   for Ready families, whose URL is often already ?family=<fam>),
    //   setSp is a no-op and no re-render fires. Scrolling the active
    //   editor into view still needs to happen so the contractor can
    //   see the panel — otherwise the click looks like "nothing
    //   happened". We schedule the scroll on the next animation frame
    //   so React has committed any pending activeFamily rerender first.
    //
    //   Zero server writes. No draft mutation. Ready/Later status
    //   untouched. Guided answers hydrate cleanly via the panel's own
    //   baseline effect (no new state introduced here).
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        const el = activeEditorRef.current;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-[var(--jc-text-soft)]" data-testid="step3-loading">
        {t("step3.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="step3-documents-and-customer-defaults">
      <div className="jc-card p-5">
        <h2 className="text-xl font-display" data-testid="step3-title">{t("step3.headline")}</h2>
        <p className="text-sm text-[var(--jc-text-soft)] mt-1" data-testid="step3-subtitle">
          {t("step3.subtitle")}
        </p>
        <p className="text-xs text-[var(--jc-text-soft)] italic mt-2">{t("step3.ready_helper_text")}</p>
      </div>

      {/* Compact family rows */}
      <ol className="space-y-2" data-testid="step3-family-list">
        {FAMILY_ORDER.map((fam) => {
          const s = derived?.[fam] || "not_started";
          const isActive = activeFamily === fam;
          const needsAttention = !!familyValidation[fam];
          return (
            <li
              key={fam}
              className={`jc-card p-3 flex items-center gap-3 ${isActive ? "border-l-4 border-l-emerald-500" : ""}`}
              data-testid={`step3-family-row-${fam}`}
              data-family={fam}
              data-status={s}
              data-needs-attention={needsAttention ? "true" : "false"}
              data-active={isActive ? "true" : "false"}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{t(`step3.family_titles.${fam}`)}</div>
                <div className="text-xs text-[var(--jc-text-soft)] truncate">{t(`step3.family_purposes.${fam}`)}</div>
              </div>
              {needsAttention && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800"
                  data-testid={`step3-family-needs-attention-${fam}`}
                >
                  {t("step3.needs_attention_pill")}
                </span>
              )}
              <StatusBadge status={s} t={t} />
              <button
                type="button"
                onClick={() => goToFamily(fam)}
                data-testid={`step3-family-row-${fam}-action`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50"
              >
                {t(`step3.family_action_labels.${s}`)}
                <ArrowRight size={12} />
              </button>
            </li>
          );
        })}
      </ol>

      {step3Done && (
        <div
          className="jc-card p-4 border-l-4 border-l-emerald-500 bg-emerald-50"
          data-testid="step3-complete-summary"
        >
          <div className="font-semibold text-sm">{t("step3.complete_title")}</div>
          <p className="text-xs text-[var(--jc-text-soft)] mt-1">
            {t("step3.complete_body")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate("/app/setup?step=review_and_ready")}
              data-testid="step3-continue-step4"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary"
            >
              {t("step3.continue_to_review")}
              <ArrowRight size={14} />
            </button>
            <button
              type="button"
              onClick={() => goToFamily(activeFamily || "proposal")}
              data-testid="step3-review-defaults"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50"
            >
              {t("step3.review_defaults")}
            </button>
          </div>
        </div>
      )}

      {/* Slice 2C4 · Placeholder-blocked incomplete summary. Renders in
          place of the complete summary whenever any family has a blocking
          validation issue — even if every family already has an explicit
          Ready / Later status. The Continue-to-Review button is NEVER
          rendered while blocked (not merely disabled). */}
      {!step3Done && firstBlockedFamily && (
        <div
          className="jc-card p-4 border-l-4 border-l-red-500 bg-red-50"
          data-testid="step3-blocked-summary"
        >
          <div className="font-semibold text-sm text-red-900">
            {t("step3.blocked_title")}
          </div>
          <p className="text-xs text-red-900/80 mt-1">
            {t("step3.blocked_body")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => goToFamily(firstBlockedFamily)}
              data-testid="step3-blocked-goto-family"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary"
            >
              {t("step3.fix_family", t(`step3.family_titles.${firstBlockedFamily}`) || FAMILY_META[firstBlockedFamily]?.title || firstBlockedFamily)}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Expanded editor for exactly ONE family */}
      <div ref={activeEditorRef} data-testid="step3-active-family-editor" data-family={activeFamily}>
        {activeIsCloseout ? (
          <DocumentDefaultsFamilyCard
            key={CLOSEOUT_FAMILY.key}
            familyKey={CLOSEOUT_FAMILY.key}
            title={CLOSEOUT_FAMILY.title}
            purpose={CLOSEOUT_FAMILY.purpose}
            snapshotNote={CLOSEOUT_FAMILY.snapshotNote}
            fields={CLOSEOUT_FAMILY.fields}
            values={cloValues}
            onChange={onCloChange}
            endpoint={CLOSEOUT_FAMILY.endpoint}
            saveButtonLabel={CLOSEOUT_FAMILY.saveButtonLabel}
            successMessage={CLOSEOUT_FAMILY.successMessage}
            userId={userId}
            baseline={cloBaseline}
            baselineUpdatedAt={cloBaselineUpdatedAt}
            onBaselineReplace={replaceCloFamilyBaselineWithRefresh}
            onFamilyValuesReplace={replaceCloFamilyValues}
            refetchFamily={refetchForCloseout}
            onCardStateChange={setCardState}
            guidedMode={buildGuidedMode("closeout")}
          />
        ) : activeIsWarranty ? (
          <DocumentDefaultsFamilyCard
            key={WARRANTY_FAMILY.key}
            familyKey={WARRANTY_FAMILY.key}
            title={WARRANTY_FAMILY.title}
            purpose={WARRANTY_FAMILY.purpose}
            snapshotNote={WARRANTY_FAMILY.snapshotNote}
            fields={WARRANTY_FAMILY.fields}
            values={cloValues}
            onChange={onCloChange}
            endpoint={WARRANTY_FAMILY.endpoint}
            saveButtonLabel={WARRANTY_FAMILY.saveButtonLabel}
            successMessage={WARRANTY_FAMILY.successMessage}
            userId={userId}
            baseline={cloBaseline}
            baselineUpdatedAt={cloBaselineUpdatedAt}
            onBaselineReplace={replaceCloFamilyBaselineWithRefresh}
            onFamilyValuesReplace={replaceCloFamilyValues}
            refetchFamily={refetchForCloseout}
            onCardStateChange={setCardState}
            guidedMode={buildGuidedMode("warranty")}
          />
        ) : (
          activeDocFamily && (
            <DocumentDefaultsFamilyCard
              key={activeDocFamily.key}
              familyKey={activeDocFamily.key}
              title={activeDocFamily.title}
              purpose={activeDocFamily.purpose}
              snapshotNote={activeDocFamily.snapshotNote}
              fields={activeDocFamily.fields}
              values={docValues}
              onChange={onDocChange}
              endpoint={activeDocFamily.endpoint}
              saveButtonLabel={activeDocFamily.saveButtonLabel}
              successMessage={activeDocFamily.successMessage}
              userId={userId}
              baseline={docBaseline}
              baselineUpdatedAt={docBaselineUpdatedAt}
              onBaselineReplace={replaceDocFamilyBaselineWithRefresh}
              onFamilyValuesReplace={replaceDocFamilyValues}
              refetchFamily={refetchForDoc(activeDocFamily)}
              onCardStateChange={setCardState}
              guidedMode={buildGuidedMode(activeDocFamily.key)}
            />
          )
        )}
      </div>

      {/* Status actions for the currently expanded family */}
      <div className="jc-card p-4" data-testid={`step3-status-actions-${activeFamily}`}>
        <div className="text-xs text-[var(--jc-text-soft)] mb-2">
          {t("step3.current_status")} <StatusBadge status={activeStatus} t={t} />
        </div>
        {disabledReason && (
          <div
            className="mb-3 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700"
            data-testid={`step3-status-disabled-reason-${activeFamily}`}
          >
            {disabledReason}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onMarkReady}
            disabled={!canRunStatusAction}
            data-testid={`step3-mark-ready-${activeFamily}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
          >
            <CheckCircle2 size={14} /> {t("step3.mark_ready")}
          </button>
          <button
            type="button"
            onClick={onOpenLaterConfirm}
            disabled={!canRunStatusAction}
            data-testid={`step3-set-up-later-${activeFamily}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            <Pause size={14} /> {t("step3.set_up_later")}
          </button>
        </div>
      </div>

      <AlertDialog open={laterConfirmOpen} onOpenChange={setLaterConfirmOpen}>
        <AlertDialogContent data-testid={`step3-later-dialog-${activeFamily}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Set up this section later?</AlertDialogTitle>
            <AlertDialogDescription>
              You can return at any time. JobClose AI will use its available built-in defaults where supported until you customize this section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`step3-later-cancel-${activeFamily}`}>
              Keep setting it up
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmLater}
              data-testid={`step3-later-confirm-${activeFamily}`}
            >
              Set up later
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Step3DocumentDefaults;
